import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { UnifiedModal, ModalButton } from '@/components/common/UnifiedModal';
import { StackingRow } from '@/components/shared/redesign/StackingRow';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

const WarningIcon = SVG_COMPONENTS.WARNING;

interface ConfirmationModalProps {
  visible: boolean;
  message: string;
  onCancel: () => void;
  onProceed: () => void;
  cancelText?: string;
  proceedText?: string;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  visible,
  message,
  onCancel,
  onProceed,
  cancelText = 'No, cancel',
  proceedText = 'Proceed',
}) => {
  const { colors } = useThemeColors();

  const footer = (
    <StackingRow style={styles.footerButtons}>
      <ModalButton label={cancelText} onPress={onCancel} variant="secondary" />
      <ModalButton label={proceedText} onPress={onProceed} variant="primary" />
    </StackingRow>
  );

  return (
    <UnifiedModal
      visible={visible}
      onClose={onCancel}
      variant="dialog"
      dialogSize="compact"
      showHeader={true}
      footer={footer}
      scrollable
      contentContainerStyle={styles.scrollContent}
    >
      <View style={styles.content}>
        <View style={styles.messageBox}>
          <View style={styles.iconContainer}>
            <WarningIcon width={48} height={48} color={colors.text.muted} />
          </View>
          <Text style={[styles.messageText, { color: colors.text.primary }]}>{message}</Text>
        </View>
      </View>
    </UnifiedModal>
  );
};

const styles = StyleSheet.create({
  content: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  // The scroll container pads 24 at the bottom; messageBox already has its 24.
  scrollContent: {
    paddingBottom: 0,
  },
  messageBox: {
    paddingHorizontal: 8, // + the modal's scroll container inset (24) = the old 32
    paddingVertical: 24,
    alignItems: 'center',
    gap: 16,
  },
  iconContainer: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageText: {
    fontSize: 16,
    fontWeight: '400',
    textAlign: 'center',
    lineHeight: 24,
  },
  footerButtons: {
    flexDirection: 'row',
    gap: 16,
  },
});
